import { useEffect, useMemo, useState } from 'react'
import { Command, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import styles from './CommandMenu.module.css'

type CommandAction = {
  id: string
  label: string
  description?: string
  shortcut?: string
  group?: string
  icon?: React.ComponentType<{ size?: number; className?: string }>
  onSelect: () => void
}

type CommandMenuProps = {
  open: boolean
  actions: CommandAction[]
  onClose: () => void
}

const CommandMenu = ({ open, actions, onClose }: CommandMenuProps) => {
  const [query, setQuery] = useState('')

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    if (open) {
      window.addEventListener('keydown', handler)
    }

    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  useEffect(() => {
    if (!open) {
      setQuery('')
    }
  }, [open])

  const filtered = useMemo(() => {
    const trimmed = query.trim().toLowerCase()
    if (!trimmed) return actions

    return actions.filter((action) => {
      const haystack = [action.label, action.description, action.group]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      return haystack.includes(trimmed)
    })
  }, [actions, query])

  const grouped = useMemo(() => {
    return filtered.reduce<Record<string, CommandAction[]>>((acc, action) => {
      const key = action.group ?? 'General'
      if (!acc[key]) acc[key] = []
      acc[key].push(action)
      return acc
    }, {})
  }, [filtered])

  if (!open) return null

  return (
    <div className={styles.overlay} role="presentation">
      <div role="dialog" aria-modal="true" className={styles.container}>
        <header className={styles.header}>
          <Search size={16} className={styles.searchIcon} aria-hidden="true" />
          <Input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search actions, pages or commands"
            className={styles.input}
            aria-label="Search workspace actions"
          />
          <span className={styles.shortcutHint} aria-hidden="true">
            <kbd>
              <Command size={14} />
            </kbd>
            <kbd>K</kbd>
          </span>
        </header>

        <div className={styles.results} role="list">
          {Object.entries(grouped).map(([group, groupActions]) => (
            <section key={group} className={styles.group} aria-label={group}>
              <div className={styles.groupLabel}>{group}</div>
              <ul className={styles.groupList}>
                {groupActions.map((action) => {
                  const Icon = action.icon
                  return (
                    <li key={action.id}>
                      <button
                        type="button"
                        className={styles.result}
                        onClick={() => {
                          action.onSelect()
                          onClose()
                        }}
                      >
                        <div className={styles.resultMain}>
                          {Icon && <Icon size={16} className={styles.resultIcon} aria-hidden="true" />}
                          <div>
                            <div className={styles.resultLabel}>{action.label}</div>
                            {action.description && (
                              <p className={styles.resultDescription}>{action.description}</p>
                            )}
                          </div>
                        </div>
                        {action.shortcut && (
                          <div className={styles.resultShortcut} aria-hidden="true">
                            {action.shortcut.split('+').map((key) => (
                              <kbd key={key}>{key}</kbd>
                            ))}
                          </div>
                        )}
                      </button>
                    </li>
                  )
                })}
              </ul>
            </section>
          ))}

          {filtered.length === 0 && (
            <div className={styles.empty}>
              <p>No matching actions found.</p>
              <Button variant="secondary" onClick={onClose} size="sm">
                Close
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export type { CommandAction, CommandMenuProps }
export default CommandMenu
